import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Loader2, Plus, Repeat, Calendar, CreditCard } from "lucide-react";
import { useRecurringTransactions, RecurringSeries } from "@/hooks/use-recurring-transactions";
import { RecurringSeriesCard } from "@/components/transactions/RecurringSeriesCard";
import { RecurringSeriesModal } from "@/components/transactions/RecurringSeriesModal";
import { TransactionModal } from "@/components/transactions/TransactionModal";

const Recurring = () => {
  const [filter, setFilter] = useState<"all" | "installment" | "fixed">("all");
  const [selectedSeries, setSelectedSeries] = useState<RecurringSeries | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [seriesToDelete, setSeriesToDelete] = useState<string | null>(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);

  const { series, loading, markAsPaid, deleteSeries, refetch } = useRecurringTransactions();

  const filteredSeries = series.filter(s => {
    if (filter === "all") return true;
    if (filter === "installment") return !!s.totalInstallments;
    if (filter === "fixed") return !s.totalInstallments;
    return true;
  });

  const handleViewDetails = (s: RecurringSeries) => {
    setSelectedSeries(s);
    setShowDetailsModal(true);
  };

  const handleDeleteClick = (seriesId: string) => {
    setSeriesToDelete(seriesId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (seriesToDelete) {
      await deleteSeries(seriesToDelete);
      setShowDeleteDialog(false);
      setSeriesToDelete(null);
    }
  };

  const handleMarkAsPaid = async (transactionId: string) => {
    await markAsPaid(transactionId);
    // Refresh the selected series
    if (selectedSeries) {
      const updated = series.find(s => s.id === selectedSeries.id);
      if (updated) setSelectedSeries(updated);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  // Calculate summaries
  const totalInstallments = series.filter(s => s.totalInstallments).length;
  const totalFixed = series.filter(s => !s.totalInstallments).length;
  const totalMonthlyAmount = series
    .filter(s => s.isActive)
    .reduce((sum, s) => sum + s.amount, 0);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Repeat className="h-6 w-6 text-primary" />
          <h1 className="text-xl sm:text-2xl font-bold">Transações Recorrentes</h1>
        </div>
        <Button size="sm" onClick={() => setShowTransactionModal(true)}>
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Nova Recorrência</span>
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="p-4 rounded-lg bg-card border">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <CreditCard className="h-4 w-4" />
            <span className="text-sm">Parcelamentos</span>
          </div>
          <p className="text-xl sm:text-2xl font-bold">{totalInstallments}</p>
        </div>
        <div className="p-4 rounded-lg bg-card border">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Calendar className="h-4 w-4" />
            <span className="text-sm">Fixas</span>
          </div>
          <p className="text-xl sm:text-2xl font-bold">{totalFixed}</p>
        </div>
        <div className="p-4 rounded-lg bg-card border">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Repeat className="h-4 w-4" />
            <span className="text-sm">Total Mensal</span>
          </div>
          <p className="text-xl sm:text-2xl font-bold">{formatCurrency(totalMonthlyAmount)}</p>
        </div>
      </div>

      {/* Filters */}
      <Tabs value={filter} onValueChange={(v) => setFilter(v as any)}>
        <TabsList>
          <TabsTrigger value="all">
            Todas ({series.length})
          </TabsTrigger>
          <TabsTrigger value="installment">
            Parcelas ({totalInstallments})
          </TabsTrigger>
          <TabsTrigger value="fixed">
            Fixas ({totalFixed})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={filter} className="mt-4 space-y-3">
          {filteredSeries.length === 0 ? (
            <div className="text-center py-12">
              <Repeat className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">Nenhuma transação recorrente</h3>
              <p className="text-muted-foreground mb-4">
                Crie sua primeira transação parcelada ou fixa
              </p>
              <Button onClick={() => setShowTransactionModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Transação Recorrente
              </Button>
            </div>
          ) : (
            filteredSeries.map((s) => (
              <RecurringSeriesCard
                key={s.id}
                series={s}
                onViewDetails={handleViewDetails}
                onDelete={handleDeleteClick}
              />
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <RecurringSeriesModal
        series={selectedSeries}
        open={showDetailsModal}
        onOpenChange={setShowDetailsModal}
        onMarkAsPaid={handleMarkAsPaid}
      />

      <TransactionModal
        open={showTransactionModal}
        onOpenChange={setShowTransactionModal}
        onSuccess={refetch}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Encerrar série?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação irá excluir todas as parcelas futuras não pagas. 
              As parcelas já pagas serão mantidas no histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Encerrar Série
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Recurring;
